import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useCommonAreas } from "@/hooks/useCommonAreas";
import { supabase } from "@/integrations/supabase/client";

interface Area {
  id: string;
  name: string;
  description: string | null;
  max_capacity: number;
  operating_hours: string;
  image_emoji: string | null;
  status: string;
}

interface EditarAreaComumModalProps {
  children: React.ReactNode;
  area: Area;
}

const EditarAreaComumModal = ({ children, area }: EditarAreaComumModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { refetch } = useCommonAreas();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    max_capacity: "",
    operating_hours: "",
    image_emoji: "",
    status: "available" as const
  });

  useEffect(() => {
    if (area) {
      setFormData({
        name: area.name || "",
        description: area.description || "",
        max_capacity: area.max_capacity?.toString() || "",
        operating_hours: area.operating_hours || "08:00 - 18:00",
        image_emoji: area.image_emoji || "🏢",
        status: area.status as any || "available"
      });
    }
  }, [area]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase
        .from("common_areas")
        .update({
          name: formData.name,
          description: formData.description || null,
          max_capacity: parseInt(formData.max_capacity) || 10,
          operating_hours: formData.operating_hours,
          image_emoji: formData.image_emoji,
          status: formData.status,
        })
        .eq("id", area.id);

      if (error) throw error;

      toast.success("Área comum atualizada com sucesso!");
      setIsOpen(false);
      refetch();
    } catch (error) {
      console.error("Erro ao atualizar área comum:", error);
      toast.error("Erro ao atualizar área comum");
    } finally {
      setIsLoading(false);
    }
  };

  const emojiOptions = [
    { value: "🏢", label: "🏢 Prédio" },
    { value: "🏊", label: "🏊 Piscina" },
    { value: "🎾", label: "🎾 Quadra" },
    { value: "🎉", label: "🎉 Salão de Festas" },
    { value: "🏋️", label: "🏋️ Academia" },
    { value: "🎮", label: "🎮 Jogos" },
    { value: "📚", label: "📚 Biblioteca" },
    { value: "🚗", label: "🚗 Garagem" },
    { value: "🌳", label: "🌳 Jardim" },
    { value: "☕", label: "☕ Área de Convivência" }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Editar Área Comum</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome da Área</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Piscina, Salão de Festas..."
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emoji">Ícone</Label>
              <Select value={formData.image_emoji} onValueChange={(value) => setFormData({ ...formData, image_emoji: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {emojiOptions.map((emoji) => (
                    <SelectItem key={emoji.value} value={emoji.value}>
                      {emoji.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descrição da área comum..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="capacity">Capacidade Máxima</Label>
              <Input
                id="capacity"
                type="number"
                value={formData.max_capacity}
                onChange={(e) => setFormData({ ...formData, max_capacity: e.target.value })}
                placeholder="10"
                min="1"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value: any) => setFormData({ ...formData, status: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Disponível</SelectItem>
                  <SelectItem value="maintenance">Manutenção</SelectItem>
                  <SelectItem value="occupied">Ocupado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="hours">Horário de Funcionamento</Label>
            <Input
              id="hours"
              value={formData.operating_hours}
              onChange={(e) => setFormData({ ...formData, operating_hours: e.target.value })}
              placeholder="08:00 - 18:00"
              required
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditarAreaComumModal;